/**
 * Word cloud layout engine
 *
 * TypeScript port of the d3 cloud layout algorithm (originally by Jason Davies).
 * Algorithm due to Jonathan Feinberg: http://static.mrfeinberg.com/bv_ch03.pdf
 *
 * Features:
 * - Collision detection using bitmap sprites
 * - Mask support for shaped word clouds
 * - Repeatable layouts via seeded random
 * - Archimedean and rectangular spiral placement
 */

// =============================================================================
// Types
// =============================================================================

export interface WordData {
  text: string
  font: string
  style: string
  weight: string
  rotate: number
  size: number
  padding: number
  x: number
  y: number
  x0: number
  y0: number
  x1: number
  y1: number
  width: number
  height: number
  xoff: number
  yoff: number
  hasText: boolean
  sprite?: number[]
  usedAngles?: Record<number, boolean>
  [key: string]: unknown
}

export interface Bounds {
  x: number
  y: number
}

export interface CloudLayoutResult {
  tags: WordData[]
  bounds: Bounds[] | null
}

type SpiralFunction = (t: number) => [number, number] | null

// =============================================================================
// Constants
// =============================================================================

const CLOUD_RADIANS = Math.PI / 180
const CW = (1 << 11) >> 5 // Canvas width in 32-bit blocks
const CH = 1 << 11 // Canvas height

// Runtime guards (headless only, see setBoundedRuntime). The scale-down restart loop has
// no natural exit: a corpus the canvas cannot hold keeps shrinking and restarting, and the
// font size never reaches a value that ends it. 40 restarts is k ~= 0.95^40 ~= 0.13 of the
// original size; the deadline covers the other half of the problem — each restart is a full
// synchronous word x angle sweep with canvas measurements, so a bounded count can still
// block the main thread for tens of seconds on a large corpus.
const MAX_RESTARTS = 40
const LAYOUT_DEADLINE_MS = 8000

// Pre-generated random numbers for repeatable clouds
const PRE_GENERATED_RANDS = [
  0.6868, 0.7893, 0.0932, 0.0671, 0.178, 0.499, 0.782, 0.9963, 0.9471, 0.009, 0.7004, 0.4932, 0.5081, 0.9142, 0.7984,
  0.0568, 0.8656, 0.8974, 0.4707, 0.7643, 0.3195, 0.4193, 0.2912, 0.6625, 0.0673, 0.3797, 0.4639, 0.8222, 0.4782,
  0.1794, 0.4986, 0.7876, 0.5792, 0.8034, 0.7903, 0.0402, 0.4574, 0.3668, 0.5482, 0.3948, 0.076, 0.3982, 0.0522, 0.7342,
  0.1242, 0.1097, 0.36, 0.2594, 0.4376, 0.1419, 0.6262, 0.9826, 0.9622, 0.9711, 0.0077, 0.0312, 0.4758, 0.9288, 0.8329,
  0.8999, 0.0037, 0.5413, 0.0802, 0.1156, 0.5554, 0.884, 0.2283, 0.3119, 0.0429, 0.9305, 0.6376, 0.4382, 0.8186, 0.9716,
  0.0294, 0.9372, 0.0857, 0.7178, 0.0253, 0.7234, 0.8033, 0.9484, 0.2826, 0.2746, 0.3579, 0.5247, 0.5853, 0.6323,
  0.1719, 0.7088, 0.3582, 0.6164, 0.55, 0.5291, 0.6613, 0.8036, 0.2047, 0.4611, 0.3304, 0.2756, 0.7736, 0.0233, 0.0977,
  0.2003, 0.148, 0.2531, 0.6425, 0.7277, 0.8584, 0.3402, 0.1581, 0.3159, 0.2387, 0.5205, 0.1088, 0.1833, 0.5413, 0.9908,
  0.2702, 0.9055, 0.639, 0.2466, 0.0414, 0.7752, 0.8377, 0.4319, 0.5592, 0.1902, 0.277, 0.2925, 0.2076, 0.4345, 0.6201,
  0.9314, 0.3671, 0.0719, 0.0839, 0.2269, 0.5309, 0.3572, 0.5612, 0.5559, 0.9306, 0.8959, 0.5367, 0.1938, 0.352, 0.4339,
  0.3679, 0.6626, 0.8313, 0.9674, 0.5897, 0.8023, 0.9511, 0.6341, 0.5692, 0.5733, 0.8618, 0.6384, 0.6847, 0.5043,
  0.8434, 0.7061, 0.1696, 0.442, 0.3028, 0.7841, 0.1405, 0.3043, 0.9792, 0.9038, 0.3295, 0.6455, 0.7712, 0.0722, 0.9125,
  0.8909, 0.9836, 0.9263, 0.4565, 0.4421, 0.474, 0.118, 0.2073, 0.1338, 0.2277, 0.6468, 0.9066, 0.4708, 0.3004, 0.8774,
  0.4506, 0.0356, 0.04, 0.3435, 0.1799, 0.8016, 0.3113, 0.1437, 0.586, 0.4913, 0.9262, 0.7401, 0.7607, 0.9649, 0.4943,
  0.8904, 0.8991, 0.1723, 0.3114, 0.5473, 0.9529, 0.1398, 0.1287, 0.4544, 0.254, 0.3877, 0.3409, 0.1798, 0.943, 0.7081,
  0.12, 0.1712, 0.043, 0.9369, 0.4889, 0.9699, 0.5242, 0.2062, 0.102, 0.7859, 0.7766, 0.1635, 0.5353, 0.6166, 0.1303,
  0.0032, 0.7985, 0.1969, 0.3748, 0.3005, 0.5932, 0.0702, 0.1975, 0.4185, 0.131, 0.4013, 0.1112, 0.8316, 0.6234, 0.4074,
  0.451, 0.0147, 0.0996, 0.5389, 0.5648, 0.409, 0.7353, 0.6717, 0.6946, 0.4944, 0.7212, 0.5502, 0.6254, 0.0399, 0.6714,
  0.8304, 0.9857, 0.3174, 0.4216, 0.696, 0.6341, 0.3131, 0.3467, 0.0879, 0.5644, 0.7309, 0.5532, 0.9988, 0.0528, 0.5452,
  0.4727, 0.7255, 0.4272, 0.4169, 0.3824, 0.3555, 0.2433, 0.9696, 0.2734, 0.8012, 0.2755, 0.626, 0.9072, 0.4329, 0.4689,
  0.2913, 0.0249, 0.867, 0.2374, 0.9982, 0.112, 0.9849, 0.9212, 0.4691, 0.6098, 0.0834, 0.2212, 0.7144, 0.9551, 0.6159,
  0.6547, 0.4241, 0.6324, 0.1243, 0.1966, 0.5406, 0.5869, 0.0767, 0.2535, 0.7326, 0.8798, 0.4162, 0.8703, 0.8057,
  0.6253, 0.6473, 0.3125, 0.4763, 0.581, 0.6875, 0.0251, 0.5084, 0.151, 0.1735, 0.9208, 0.5753, 0.5098, 0.0259, 0.0274,
  0.6355, 0.9876, 0.0463, 0.5058, 0.0598, 0.782, 0.5012, 0.4784, 0.7387, 0.6355, 0.8133, 0.693, 0.6058, 0.9761, 0.3867,
  0.0003, 0.0328, 0.6108, 0.4098, 0.759, 0.0627, 0.1321, 0.2379, 0.4584, 0.8484, 0.984, 0.7913, 0.0988, 0.6989, 0.0783,
  0.3065, 0.7854, 0.1204, 0.8148, 0.362, 0.2366, 0.4739, 0.9696, 0.5709, 0.4768, 0.8758, 0.7809, 0.7928, 0.6216, 0.6804,
  0.1501, 0.2829, 0.9073, 0.3587, 0.8273, 0.9005, 0.5036, 0.2171, 0.7824, 0.2368, 0.7976, 0.16, 0.1329, 0.1366, 0.8832,
  0.2642, 0.0886, 0.0644, 0.7552, 0.7483, 0.9991, 0.0322, 0.7605, 0.376, 0.5544, 0.9725, 0.779, 0.8274, 0.2965, 0.4585,
  0.6771, 0.809, 0.7277, 0.4169, 0.6538, 0.0333, 0.5759, 0.5762, 0.1887, 0.5388, 0.4046, 0.0541, 0.2646, 0.804, 0.2282,
  0.7802, 0.0893, 0.8491, 0.2647, 0.3501, 0.4156, 0.1729, 0.7022, 0.6037, 0.1069, 0.6565, 0.9502, 0.3761, 0.1864,
  0.4262, 0.0734, 0.4251, 0.3151, 0.5355, 0.4927, 0.0518, 0.5352, 0.9582, 0.1672, 0.7825, 0.0352, 0.9528, 0.603, 0.072,
  0.6135, 0.2845, 0.8009, 0.0266, 0.7951, 0.1652, 0.4443, 0.3102, 0.2476, 0.6791, 0.2064, 0.3061, 0.4564, 0.8991,
  0.8637, 0.5125, 0.6673, 0.0524, 0.1336, 0.6751, 0.9833, 0.405, 0.0334, 0.1339, 0.8281, 0.5243, 0.7002, 0.3849, 0.8131,
  0.8348, 0.7903, 0.8398, 0.8199, 0.1777, 0.993, 0.5527, 0.7312, 0.8931, 0.8746, 0.0093, 0.9779, 0.8186, 0.4465, 0.8763,
  0.5554, 0.1658, 0.2972, 0.309, 0.8765, 0.9086, 0.9454, 0.2443, 0.7, 0.5668, 0.5616, 0.1426, 0.9988, 0.7989, 0.4451,
  0.8463, 0.2935, 0.7445, 0.3038, 0.6435, 0.0289, 0.3415, 0.7662, 0.5788, 0.7812, 0.7289, 0.9724, 0.2244, 0.2365,
  0.7867, 0.4282, 0.8364, 0.9836, 0.9808, 0.2062, 0.6717, 0.6864, 0.4389, 0.1943, 0.853, 0.209, 0.6608, 0.8858, 0.2983,
  0.4457, 0.093, 0.6039, 0.0496, 0.858, 0.6229, 0.2592, 0.6074, 0.4345, 0.0559, 0.6621, 0.1249, 0.3579, 0.3436, 0.9871,
  0.5547, 0.4646, 0.4637, 0.1756, 0.5795, 0.815, 0.4392, 0.761, 0.318, 0.8845, 0.6421, 0.4452, 0.1726, 0.4825, 0.4175,
  0.8778, 0.9873, 0.8618, 0.4965, 0.0123, 0.4522, 0.1862, 0.1015, 0.7882, 0.9244, 0.8976, 0.6936, 0.155, 0.4405, 0.5316,
  0.3338, 0.8912, 0.9108, 0.5881, 0.207, 0.2004, 0.0605, 0.2439, 0.2253, 0.2748, 0.9184, 0.9917, 0.9894, 0.6743, 0.4684,
  0.5059, 0.8505, 0.0808, 0.7489, 0.8729, 0.9741, 0.8655, 0.7135, 0.2203, 0.4636, 0.8983, 0.6001, 0.15, 0.7511, 0.5595,
  0.786, 0.2741, 0.5518, 0.0105, 0.2179, 0.259, 0.4971, 0.5548, 0.3951, 0.724, 0.0593, 0.7114, 0.8663, 0.2544, 0.1487,
  0.1397, 0.8374, 0.3055, 0.037, 0.1633, 0.2621, 0.48, 0.1121, 0.3283, 0.7006, 0.534, 0.3452, 0.9642, 0.0405, 0.7097,
  0.539, 0.9655, 0.2128, 0.1193, 0.7648, 0.353, 0.8561, 0.1574, 0.725, 0.5348, 0.5479, 0.7499, 0.4054, 0.6815, 0.2348,
  0.8709, 0.6292, 0.0444, 0.9255, 0.8353, 0.0014, 0.2117, 0.8905, 0.2841, 0.5134, 0.026, 0.3529, 0.7921, 0.6067, 0.2237,
  0.5177, 0.4283, 0.689, 0.7014, 0.6911, 0.6278, 0.0396, 0.05, 0.8562, 0.4886, 0.9526, 0.1502, 0.8683, 0.8812, 0.524,
  0.9992, 0.4335, 0.811, 0.5798, 0.987, 0.4596, 0.339, 0.8562, 0.848, 0.9394, 0.7434, 0.2933, 0.1153, 0.8989, 0.0044,
  0.4823, 0.0032, 0.5481, 0.9475, 0.9003, 0.0759, 0.842, 0.5382, 0.3882, 0.876, 0.6839, 0.5837, 0.5395, 0.8983, 0.7606,
  0.1725, 0.2702, 0.0956, 0.3491, 0.0573, 0.593, 0.7504, 0.3236, 0.419, 0.9947, 0.0573, 0.0518, 0.3442, 0.0788, 0.6635,
  0.6893, 0.2282, 0.5391, 0.9224, 0.8142, 0.0359, 0.8814
]

// =============================================================================
// Spiral Functions
// =============================================================================

// Spiral cache for performance optimization
const archimedeanSpiralCache: Record<number, [number, number]> = {}

/**
 * Archimedean spiral function for word placement
 */
function createArchimedeanSpiral(size: [number, number]): SpiralFunction {
  const e = size[0] / size[1]
  return (t: number): [number, number] | null => {
    if (archimedeanSpiralCache[t] === undefined) {
      const tt = t * 0.1
      archimedeanSpiralCache[t] = [e * tt * Math.cos(tt), tt * Math.sin(tt)]
    }
    return archimedeanSpiralCache[t]
  }
}

/**
 * Rectangular spiral function for word placement
 */
function createRectangularSpiral(size: [number, number]): SpiralFunction {
  const dy = 4
  const dx = (dy * size[0]) / size[1]
  let x = 0
  let y = 0

  return (t: number): [number, number] => {
    const sign = t < 0 ? -1 : 1
    switch ((Math.sqrt(1 + 4 * sign * t) - sign) & 3) {
      case 0:
        x += dx
        break
      case 1:
        y += dy
        break
      case 2:
        x -= dx
        break
      default:
        y -= dy
        break
    }
    return [x, y]
  }
}

const SPIRALS: Record<string, (size: [number, number]) => SpiralFunction> = {
  archimedean: createArchimedeanSpiral,
  rectangular: createRectangularSpiral
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Initialize board array with mask or zeros
 */
function zeroArray(mask: number[] | null, n: number): number[] {
  if (mask === null || mask.length === 0) {
    const a: number[] = []
    for (let i = 0; i < n; i++) a[i] = 0
    return a
  }
  return mask.slice(0)
}

/**
 * Check if tag collides with existing words on the board
 */
function cloudCollide(tag: WordData, board: number[], sw: number): boolean {
  // eslint-disable-next-line no-param-reassign
  sw >>= 5

  const sprite = tag.sprite
  if (!sprite) return false

  const w = tag.width >> 5
  const lx = tag.x - (w << 4)
  const sx = lx & 0x7f
  const msx = 32 - sx
  const h = tag.y1 - tag.y0
  let x = (tag.y + tag.y0) * sw + (lx >> 5)
  let last: number

  for (let j = 0; j < h; j++) {
    last = 0
    for (let i = 0; i <= w; i++) {
      if (((last << msx) | (i < w ? (last = sprite[j * w + i]) >>> sx : 0)) & board[x + i]) {
        return true
      }
    }
    x += sw
  }
  return false
}

/**
 * Update bounds to include new word
 */
function cloudBounds(bounds: Bounds[], d: WordData): void {
  const b0 = bounds[0]
  const b1 = bounds[1]
  if (d.x + d.x0 < b0.x) b0.x = d.x + d.x0
  if (d.y + d.y0 < b0.y) b0.y = d.y + d.y0
  if (d.x + d.x1 > b1.x) b1.x = d.x + d.x1
  if (d.y + d.y1 > b1.y) b1.y = d.y + d.y1
}

/**
 * Check if rectangles overlap
 */
function collideRects(a: WordData, b: Bounds[]): boolean {
  return a.x + a.x1 > b[0].x && a.x + a.x0 < b[1].x && a.y + a.y1 > b[0].y && a.y + a.y0 < b[1].y
}

/**
 * Render word sprites to canvas and extract bitmap data
 */
function cloudSprite(d: WordData, data: WordData[], di: number, ctx: CanvasRenderingContext2D, ratio: number): void {
  if (d.sprite) return

  ctx.clearRect(0, 0, (CW << 5) / ratio, CH / ratio)

  let x = 0
  let y = 0
  let maxh = 0
  const n = data.length

  // eslint-disable-next-line no-param-reassign
  --di

  // eslint-disable-next-line no-param-reassign
  while (++di < n) {
    // eslint-disable-next-line no-param-reassign
    d = data[di]

    ctx.save()
    ctx.font = `${d.style} ${d.weight} ${Math.trunc((d.size + 1) / ratio)}px ${d.font}`

    let w = ctx.measureText(d.text).width * ratio
    let h = Math.trunc(d.size * 1.5)
    if (h <= 4) h = h * 2

    if (d.rotate) {
      const sr = Math.sin(d.rotate * CLOUD_RADIANS)
      const cr = Math.cos(d.rotate * CLOUD_RADIANS)
      const wcr = w * cr
      const wsr = w * sr
      const hcr = h * cr
      const hsr = h * sr
      w = ((Math.max(Math.abs(wcr + hsr), Math.abs(wcr - hsr)) + 0x1f) >> 5) << 5
      h = Math.trunc(Math.max(Math.abs(wsr + hcr), Math.abs(wsr - hcr)))
    } else {
      w = ((w + 0x1f) >> 5) << 5
    }

    if (h > maxh) maxh = h
    if (x + w >= CW << 5) {
      x = 0
      y += maxh
      maxh = 0
    }
    if (y + h >= CH) break

    ctx.translate((x + (w >> 1)) / ratio, (y + (h >> 1)) / ratio)
    if (d.rotate) ctx.rotate(d.rotate * CLOUD_RADIANS)
    ctx.fillText(d.text, 0, 0)
    if (d.padding) {
      ctx.lineWidth = 2 * d.padding
      ctx.strokeText(d.text, 0, 0)
    }
    ctx.restore()

    d.width = w
    d.height = h
    d.xoff = x
    d.yoff = y
    d.x1 = w >> 1
    d.y1 = h >> 1
    d.x0 = -d.x1
    d.y0 = -d.y1
    d.hasText = true
    x += w
  }

  const pixels = ctx.getImageData(0, 0, (CW << 5) / ratio, CH / ratio).data
  const sprite: number[] = []

  // eslint-disable-next-line no-param-reassign
  while (--di >= 0) {
    // eslint-disable-next-line no-param-reassign
    d = data[di]
    if (!d.hasText || !d.width) continue

    const w = d.width
    const w32 = w >> 5

    let h = d.y1 - d.y0

    // Zero the buffer
    for (let i = 0; i < h * w32; i++) sprite[i] = 0

    x = d.xoff
    if (null === x) return
    y = d.yoff

    let seen = 0
    let seenRow = -1

    for (let j = 0; j < h; j++) {
      for (let i = 0; i < w; i++) {
        const k = w32 * j + (i >> 5)
        const m = pixels[((y + j) * (CW << 5) + (x + i)) << 2] ? 1 << (31 - (i % 32)) : 0
        sprite[k] |= m
        seen |= m
      }

      if (seen) {
        seenRow = j
      } else {
        d.y0++
        h--
        j--
        y++
      }
    }

    d.y1 = d.y0 + seenRow
    d.sprite = sprite.slice(0, (d.y1 - d.y0) * w32)
  }
}

// =============================================================================
// CloudLayout Class
// =============================================================================

/**
 * Cloud layout engine class
 */
export class CloudLayout {
  private preGeneratedCounter = 0
  private bringWordsCloser = false

  private size: [number, number] = [256, 256]
  private words: Array<Record<string, unknown>> = []
  private textFn: (d: Record<string, unknown>, i: number) => string = d => String(d.text || '')
  private fontFn: (d: Record<string, unknown>, i: number) => string = () => 'serif'
  private fontStyleFn: (d: Record<string, unknown>, i: number) => string = () => 'normal'
  private fontWeightFn: (d: Record<string, unknown>, i: number) => string = () => 'normal'
  private rotateFn: (d: Record<string, unknown>, i: number) => number = () => 0
  private fontSizeFn: (d: Record<string, unknown>, i: number) => number = d => Math.sqrt(Number(d.value) || 0)
  private paddingFn: (d: Record<string, unknown>, i: number) => number = () => 1
  private spiralType: string = 'archimedean'
  private mask: number[] | null = null
  private maskBound: number[] | null = null

  private boundedRuntime = false

  private _canvas: HTMLCanvasElement | null = null
  private _ctx: CanvasRenderingContext2D | null = null
  private _ratio = 1

  private fakeRand(): number {
    this.preGeneratedCounter += 1
    if (this.preGeneratedCounter >= 750) this.preGeneratedCounter = 0
    return PRE_GENERATED_RANDS[this.preGeneratedCounter]
  }

  randIndex(x: number): this {
    this.preGeneratedCounter = x
    return this
  }

  setBringWordsCloser(x: boolean): this {
    this.bringWordsCloser = x
    return this
  }

  /**
   * Opt-in bound on the placement loop: caps restarts and adds a wall-clock deadline.
   * Off by default so the live page keeps its exact current behaviour; only the headless
   * export route turns it on, where an unplaceable cloud is a hung browser rather than a
   * slow chart — the loop is synchronous, so it also blocks the page's own watchdog timer.
   */
  setBoundedRuntime(x: boolean): this {
    this.boundedRuntime = x
    return this
  }

  setMask(x: number[], b: number[]): this {
    if (x.length) {
      this.mask = x
      b[0] = Math.max(0, b[0] - 30)
      b[2] += 30
      if (b.length) this.maskBound = b
    } else {
      this.mask = null
      this.maskBound = null
    }
    return this
  }

  setSize(x: [number, number]): this {
    this.size = [Number(x[0]), (Number(x[1]) >> 5) << 5]
    return this
  }

  setWords(x: Array<Record<string, unknown>>): this {
    this.words = x
    return this
  }

  setFont(x: string | ((d: Record<string, unknown>, i: number) => string)): this {
    this.fontFn = typeof x === 'function' ? x : () => x
    return this
  }

  setFontStyle(x: string | ((d: Record<string, unknown>, i: number) => string)): this {
    this.fontStyleFn = typeof x === 'function' ? x : () => x
    return this
  }

  setFontWeight(x: string | ((d: Record<string, unknown>, i: number) => string)): this {
    this.fontWeightFn = typeof x === 'function' ? x : () => x
    return this
  }

  setRotate(x: number | ((d: Record<string, unknown>, i: number) => number)): this {
    this.rotateFn = typeof x === 'function' ? x : () => x
    return this
  }

  setText(x: string | ((d: Record<string, unknown>, i: number) => string)): this {
    this.textFn = typeof x === 'function' ? x : () => x
    return this
  }

  setSpiral(x: string): this {
    this.spiralType = x
    return this
  }

  setFontSize(x: number | ((d: Record<string, unknown>, i: number) => number)): this {
    this.fontSizeFn = typeof x === 'function' ? x : () => x
    return this
  }

  setPadding(x: number | ((d: Record<string, unknown>, i: number) => number)): this {
    this.paddingFn = typeof x === 'function' ? x : () => x
    return this
  }

  setCanvas(canvas: HTMLCanvasElement): this {
    this._canvas = canvas
    this._ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (this._ctx) {
      this._ctx.fillStyle = this._ctx.strokeStyle = 'red'
      this._ctx.textAlign = 'center'
    }
    return this
  }

  setRatio(r: number): this {
    this._ratio = r
    return this
  }

  private ensureCanvas(): void {
    if (this._ctx) return

    if (typeof document !== 'undefined') {
      this._canvas = document.createElement('canvas')
      this._canvas.width = 1
      this._canvas.height = 1
      const testCtx = this._canvas.getContext('2d')
      this._ratio = testCtx ? Math.sqrt(testCtx.getImageData(0, 0, 1, 1).data.length >> 2) : 1
      this._canvas.width = (CW << 5) / this._ratio
      this._canvas.height = CH / this._ratio
    } else {
      throw new TypeError('Canvas not available')
    }

    this._ctx = this._canvas.getContext('2d', { willReadFrequently: true })
    if (this._ctx) {
      this._ctx.fillStyle = this._ctx.strokeStyle = 'red'
      this._ctx.textAlign = 'center'
    }
  }

  private place(
    board: number[],
    tag: WordData,
    bounds: Bounds[] | null,
    maskBound: number[] | null,
    placedWords: { count: number },
    missedWords: { count: number }
  ): boolean {
    const startX = tag.x
    const startY = tag.y
    let deltaW = (this.size[0] * 3) / 4
    let deltaH = (this.size[1] * 3) / 4
    const spiralFn = SPIRALS[this.spiralType] || SPIRALS.archimedean
    const s = spiralFn(this.size)
    const dt = this.fakeRand() < 0.5 ? 1 : -1
    let t = -dt

    if (maskBound !== null) {
      deltaW = ((maskBound[2] - maskBound[0]) * 3) / 4
      deltaH = ((maskBound[3] - maskBound[1]) * 3) / 4
    }

    // Don't place word if it's bigger than canvas
    if (tag.width < this.size[0] && tag.height < this.size[1]) {
      let dxdy: [number, number] | null
      while ((dxdy = s((t += dt)))) {
        const dx = Math.trunc(dxdy[0])
        const dy = Math.trunc(dxdy[1])

        // Speed optimization
        if (Math.abs(dx) > deltaW && Math.abs(dy) > deltaH) break

        tag.x = startX + dx
        tag.y = startY + dy

        if (maskBound === null) {
          if (
            tag.x + tag.x0 < 0 ||
            tag.y + tag.y0 < 0 ||
            tag.x + tag.x1 > this.size[0] ||
            tag.y + tag.y1 > this.size[1]
          ) {
            continue
          }
        } else {
          if (
            tag.x + tag.x0 < maskBound[0] ||
            tag.y + tag.y0 < maskBound[1] ||
            tag.x + tag.x1 > maskBound[2] ||
            tag.y + tag.y1 > maskBound[3]
          ) {
            continue
          }
        }

        if (!cloudCollide(tag, board, this.size[0]) && (!bounds || collideRects(tag, bounds))) {
          const sprite = tag.sprite
          if (!sprite) continue

          const w = tag.width >> 5
          const sw = this.size[0] >> 5
          const lx = tag.x - (w << 4)
          const sx = lx & 0x7f
          const msx = 32 - sx
          const h = tag.y1 - tag.y0
          let x = (tag.y + tag.y0) * sw + (lx >> 5)
          let last: number

          for (let j = 0; j < h; j++) {
            last = 0
            for (let i = 0; i <= w; i++) {
              board[x + i] |= (last << msx) | (i < w ? (last = sprite[j * w + i]) >>> sx : 0)
            }
            x += sw
          }

          delete tag.sprite
          placedWords.count += 1
          missedWords.count = 0
          return true
        }
      }
    }

    delete tag.sprite
    missedWords.count += 1
    return false
  }

  /**
   * Synchronous layout computation - runs entire placement loop without intervals
   */
  start(): CloudLayoutResult {
    const startedAt = Date.now()
    let restarts = 0

    this.ensureCanvas()

    const ctx = this._ctx!
    const ratio = this._ratio

    const placedWords = { count: 0 }
    const missedWords = { count: 0 }

    let board = zeroArray(this.mask, (this.size[0] >> 5) * this.size[1])
    let bounds: Bounds[] | null = null

    const n = this.words.length
    let i = -1
    let k = 1
    let tags: WordData[] = []

    let data: WordData[] = this.words
      .map((d, idx) => {
        const word = d as WordData
        word.text = this.textFn(d, idx)
        word.font = this.fontFn(d, idx)
        word.style = this.fontStyleFn(d, idx)
        word.weight = this.fontWeightFn(d, idx)
        word.rotate = this.rotateFn(d, idx)
        word.size = Math.trunc(this.fontSizeFn(d, idx))
        word.padding = this.paddingFn(d, idx)
        return word
      })
      .sort((a, b) => b.size - a.size)

    // Collect unique angles
    const angleObj: Record<number, boolean> = {}
    for (let j = 0; j < data.length; j++) angleObj[data[j].rotate] = true
    const angles = Object.keys(angleObj).map(Number)

    while (++i < n) {
      if (missedWords.count > 7) break

      const d = data[i]
      let placed = false

      for (let i1 = 0; i1 < angles.length; i1++) {
        if (this.bringWordsCloser) {
          d.x = (this.size[0] * (this.fakeRand() / 3 + 0.75)) >> 1
          d.y = (this.size[1] * (this.fakeRand() / 3 + 0.75)) >> 1
        } else if (this.maskBound !== null) {
          const dx = this.maskBound[2] - this.maskBound[0]
          const dy = this.maskBound[3] - this.maskBound[1]
          d.x = Math.trunc(this.maskBound[0] + dx * this.fakeRand())
          d.y = Math.trunc(this.maskBound[1] + dy * this.fakeRand())
        } else {
          d.x = (this.size[0] * (this.fakeRand() + 0.5)) >> 1
          d.y = (this.size[1] * (this.fakeRand() + 0.5)) >> 1
        }

        cloudSprite(d, data, i, ctx, ratio)
        placed = this.place(board, d, bounds, this.maskBound, placedWords, missedWords)

        if (placed || placedWords.count > 25) break

        missedWords.count -= 1

        if (d.usedAngles === undefined) d.usedAngles = {}
        d.usedAngles[d.rotate] = true
        delete d.sprite

        for (let i2 = 0; i2 < angles.length; i2++) {
          if (d.usedAngles[angles[i2]] === undefined) {
            d.rotate = angles[i2]
            break
          }
        }
      }

      if (d.hasText && placed) {
        tags.push(d)
        if (bounds) {
          cloudBounds(bounds, d)
        } else {
          bounds = [
            { x: d.x + d.x0, y: d.y + d.y0 },
            { x: d.x + d.x1, y: d.y + d.y1 }
          ]
        }
        // Temporary hack - center coordinates
        d.x -= this.size[0] >> 1
        d.y -= this.size[1] >> 1
      } else {
        // Scale down and restart when first words can't be placed
        if (placedWords.count < 25) {
          // Checked before the reset below, so the words this pass did place survive.
          if (this.boundedRuntime && ++restarts > MAX_RESTARTS) break
          if (this.boundedRuntime && Date.now() - startedAt > LAYOUT_DEADLINE_MS) break

          placedWords.count = 0
          board = zeroArray(this.mask, (this.size[0] >> 5) * this.size[1])
          bounds = null
          i = -1
          tags = []
          k *= 0.95

          data = this.words
            .map((d, idx) => {
              const word = d as WordData
              word.text = this.textFn(d, idx)
              word.font = this.fontFn(d, idx)
              word.style = this.fontStyleFn(d, idx)
              word.weight = this.fontWeightFn(d, idx)
              word.rotate = this.rotateFn(d, idx)
              word.size = Math.trunc(this.fontSizeFn(d, idx) * k)
              if (word.size < 1) word.size = 1
              word.padding = this.paddingFn(d, idx)
              return word
            })
            .sort((a, b) => b.size - a.size)

          for (let j2 = 0; j2 < data.length; j2++) {
            delete data[j2].sprite
            delete data[j2].usedAngles
          }
        }
      }
    }

    return { tags, bounds }
  }
}

export default CloudLayout
